// admin/src/lib/storage.ts
//
// Upload/replace/delete helpers against the public `exercise-media`
// bucket (migration 0025_media_library.sql). Replacing or deleting an
// item always removes the old object first/after, so the bucket doesn't
// accumulate orphans (spec §3 Lane D: "Remove the replaced and deleted
// objects").
import { supabase, MEDIA_BUCKET, SUPABASE_URL } from './supabase';
import { publicMediaUrl } from './storagePaths';

export type UploadProgress = (fraction: number) => void;

/**
 * Uploads `blob` to `path`. supabase-js's storage upload has no native
 * progress event, so when the blob is small (typical for a resized JPEG)
 * this just reports 0 -> 1; for a video (which can be tens of MB) it
 * reports indeterminate progress via a timer so the UI still shows
 * motion instead of freezing.
 */
export async function uploadMedia(path: string, blob: Blob, contentType: string, onProgress?: UploadProgress): Promise<void> {
  onProgress?.(0);
  let indeterminate: ReturnType<typeof setInterval> | undefined;
  if (blob.size > 2_000_000) {
    let fraction = 0;
    indeterminate = setInterval(() => {
      fraction = Math.min(0.9, fraction + 0.08);
      onProgress?.(fraction);
    }, 300);
  }
  try {
    const { error } = await supabase.storage.from(MEDIA_BUCKET).upload(path, blob, {
      contentType,
      upsert: false,
    });
    if (error) throw error;
  } finally {
    if (indeterminate) clearInterval(indeterminate);
  }
  onProgress?.(1);
}

export async function deleteMedia(paths: Array<string | null | undefined>): Promise<void> {
  const real = paths.filter((p): p is string => Boolean(p));
  if (real.length === 0) return;
  const { error } = await supabase.storage.from(MEDIA_BUCKET).remove(real);
  if (error) throw error;
}

/** Replaces `oldPath` with a freshly-uploaded `blob` at a new random path, then deletes the old object. */
export async function replaceMedia(
  oldPath: string | null | undefined,
  newPath: string,
  blob: Blob,
  contentType: string,
  onProgress?: UploadProgress,
): Promise<void> {
  await uploadMedia(newPath, blob, contentType, onProgress);
  if (oldPath && oldPath !== newPath) {
    await deleteMedia([oldPath]);
  }
}

export function mediaUrl(path: string | null | undefined): string | null {
  return publicMediaUrl(SUPABASE_URL, path);
}
